import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Package, DollarSign, Clock, CheckCircle, Settings, 
  FileText, Users, ChevronRight, Loader2, TrendingUp, Mail,
  Eye, MessageCircle, Download, X, CreditCard, MapPin, User, MessageSquare,
  Building2, Bookmark
} from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
    setLoading(false);
  };

  const viewOrder = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setSelectedOrder(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const downloadReceipt = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/receipt`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/10 text-yellow-500';
      case 'confirmed': return 'bg-blue-500/10 text-blue-500';
      case 'printing': return 'bg-purple-500/10 text-purple-500';
      case 'shipped': return 'bg-[#CCFF00]/10 text-[#CCFF00]';
      case 'delivered': return 'bg-green-500/10 text-green-500';
      default: return 'bg-white/10 text-white/70';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
            <div>
              <span className="overline text-[#CCFF00]">Operations Overview</span>
              <h1 className="text-3xl font-['Chivo'] font-black text-white mt-2">
                Welcome, {user?.name?.split(' ')[0] || 'Admin'}
              </h1>
            </div>
            <div className="flex gap-3 mt-4 md:mt-0">
              <Link to="/admin/settings">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-none">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </Link>
              <Link to="/admin/orders">
                <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase rounded-none">
                  Manage Orders
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
            <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-4">
                <Package className="w-6 h-6 text-[#CCFF00]" />
                <TrendingUp className="w-4 h-4 text-green-500" />
              </div>
              <p className="text-white/50 text-sm">Total Orders</p>
              <p className="text-3xl font-bold text-white font-mono">{stats?.total_orders || 0}</p>
            </div>

            <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-4">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
              <p className="text-white/50 text-sm">Pending</p>
              <p className="text-3xl font-bold text-yellow-500 font-mono">{stats?.pending_orders || 0}</p>
            </div>

            <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-4">
                <CheckCircle className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-white/50 text-sm">Confirmed</p>
              <p className="text-3xl font-bold text-blue-500 font-mono">{stats?.confirmed_orders || 0}</p>
            </div>

            <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-4">
                <Eye className="w-6 h-6 text-purple-500" />
              </div>
              <p className="text-white/50 text-sm">Visits Today</p>
              <p className="text-3xl font-bold text-purple-500 font-mono">{stats?.today_visits || 0}</p>
              <p className="text-white/30 text-xs mt-1">Total: {stats?.total_visits || 0}</p>
            </div>

            <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-4">
                <DollarSign className="w-6 h-6 text-[#CCFF00]" />
              </div>
              <p className="text-white/50 text-sm">Revenue</p>
              <p className="text-3xl font-bold text-[#CCFF00] font-mono">
                ${stats?.total_revenue?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-12">
            <Link to="/admin/orders" className="block">
              <div className="bg-[#111] border border-white/10 p-6 rounded-sm hover:border-[#CCFF00]/50 transition-colors group relative">
                {stats?.pending_orders > 0 && (
                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                    {stats.pending_orders}
                  </div>
                )}
                <Package className="w-8 h-8 text-[#CCFF00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">Orders</h3>
                <p className="text-white/50 text-sm">
                  {stats?.pending_orders > 0 ? `${stats.pending_orders} pending` : 'View all'}
                </p>
              </div>
            </Link>

            <Link to="/admin/quote-requests" className="block">
              <div className="bg-[#111] border border-yellow-500/30 p-6 rounded-sm hover:border-yellow-500/60 transition-colors group relative">
                {stats?.pending_quotes > 0 && (
                  <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                    {stats.pending_quotes}
                  </div>
                )}
                <Mail className="w-8 h-8 text-yellow-500 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">Quotes</h3>
                <p className="text-white/50 text-sm">
                  {stats?.pending_quotes > 0 ? `${stats.pending_quotes} pending` : 'STEP/IGES'}
                </p>
              </div>
            </Link>

            <Link to="/admin/chat" className="block">
              <div className="bg-[#111] border border-green-500/30 p-6 rounded-sm hover:border-green-500/60 transition-colors group relative">
                {stats?.unread_chats > 0 && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                    {stats.unread_chats}
                  </div>
                )}
                <MessageCircle className="w-8 h-8 text-green-500 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">Support</h3>
                <p className="text-white/50 text-sm">
                  {stats?.unread_chats > 0 ? `${stats.unread_chats} unread` : 'Live Chat'}
                </p>
              </div>
            </Link>

            <Link to="/admin/saved-quotes" className="block">
              <div className="bg-[#111] border border-cyan-500/30 p-6 rounded-sm hover:border-cyan-500/60 transition-colors group">
                <Bookmark className="w-8 h-8 text-cyan-500 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">Saved</h3>
                <p className="text-white/50 text-sm">Customer Quotes</p>
              </div>
            </Link>

            <Link to="/admin/users" className="block">
              <div className="bg-[#111] border border-white/10 p-6 rounded-sm hover:border-[#CCFF00]/50 transition-colors group relative">
                {stats?.new_customers > 0 && (
                  <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {stats.new_customers}
                  </div>
                )}
                <Users className="w-8 h-8 text-[#CCFF00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">Users</h3>
                <p className="text-white/50 text-sm">& Coupons</p>
              </div>
            </Link>

            <Link to="/admin/companies" className="block">
              <div className="bg-[#111] border border-purple-500/30 p-6 rounded-sm hover:border-purple-500/60 transition-colors group">
                <Building2 className="w-8 h-8 text-purple-500 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">Companies</h3>
                <p className="text-white/50 text-sm">Group Users</p>
              </div>
            </Link>

            <Link to="/admin/settings" className="block">
              <div className="bg-[#111] border border-white/10 p-6 rounded-sm hover:border-[#CCFF00]/50 transition-colors group">
                <Settings className="w-8 h-8 text-[#CCFF00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">Pricing</h3>
                <p className="text-white/50 text-sm">Materials</p>
              </div>
            </Link>

            <Link to="/admin/cms" className="block">
              <div className="bg-[#111] border border-white/10 p-6 rounded-sm hover:border-[#CCFF00]/50 transition-colors group">
                <FileText className="w-8 h-8 text-[#CCFF00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">CMS</h3>
                <p className="text-white/50 text-sm">Content</p>
              </div>
            </Link>

            <Link to="/admin/pdf-settings" className="block">
              <div className="bg-[#111] border border-white/10 p-6 rounded-sm hover:border-[#CCFF00]/50 transition-colors group">
                <FileText className="w-8 h-8 text-[#CCFF00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">PDF</h3>
                <p className="text-white/50 text-sm">Quotes & Receipts</p>
              </div>
            </Link>
          </div>

          {/* Recent Orders */}
          <div className="bg-[#111] border border-white/10 rounded-sm">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Recent Orders</h2>
              <Link to="/admin/orders" className="text-[#CCFF00] text-sm hover:underline">
                View All
              </Link>
            </div>

            {stats?.recent_orders?.length > 0 ? (
              <div className="divide-y divide-white/10">
                {stats.recent_orders.slice(0, 5).map((order) => (
                  <div 
                    key={order.order_id}
                    className="p-6 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => viewOrder(order.order_id)}
                    data-testid={`recent-order-${order.order_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <p className="text-white font-mono font-bold">{order.order_id}</p>
                        <p className="text-white/50 text-sm">{order.customer_name}</p>
                        <p className="text-white/30 text-xs">
                          {new Date(order.created_at).toLocaleString('en-AU')}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <Badge className={`capitalize ${getStatusColor(order.order_status)}`}>
                          {order.order_status?.replace('-', ' ')}
                        </Badge>
                        <span className="text-white font-mono font-bold">
                          ${order.total?.toFixed(2)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-white/30" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No orders yet</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Package className="w-5 h-5 text-[#CCFF00]" />
                Order {selectedOrder?.order_id}
              </span>
              <Badge className={`capitalize ${getStatusColor(selectedOrder?.order_status)}`}>
                {selectedOrder?.order_status?.replace('_', ' ')}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <div className="bg-white/5 p-4 rounded">
                <h4 className="text-white/50 text-xs uppercase mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" /> Customer
                </h4>
                <p className="text-white font-medium">{selectedOrder.customer_name}</p>
                <p className="text-white/70 text-sm">{selectedOrder.customer_email}</p>
                {selectedOrder.customer_phone && (
                  <p className="text-white/50 text-sm">{selectedOrder.customer_phone}</p>
                )}
                {selectedOrder.customer_address && (
                  <p className="text-white/50 text-sm flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" /> {selectedOrder.customer_address}
                  </p>
                )}
              </div>

              {/* Payment Info */}
              <div className="bg-white/5 p-4 rounded">
                <h4 className="text-white/50 text-xs uppercase mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> Payment
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/50 text-xs">Method</p>
                    <p className="text-white capitalize">{selectedOrder.payment_method}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Status</p>
                    <Badge variant="outline" className={`text-xs capitalize ${
                      selectedOrder.payment_status === 'paid' 
                        ? 'border-green-500/30 text-green-400' 
                        : 'border-yellow-500/30 text-yellow-400'
                    }`}>
                      {selectedOrder.payment_status?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="text-white/50 text-xs uppercase mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Items ({selectedOrder.items?.length || 0})
                </h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="bg-white/5 p-3 rounded flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{item.file_name}</p>
                        <p className="text-white/50 text-xs">
                          {item.material_name} • Qty: {item.quantity}
                        </p>
                      </div>
                      <p className="text-white font-mono">${(item.total_price || item.price || 0).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Total</span>
                  <span className="text-[#CCFF00] text-2xl font-bold font-mono">
                    ${selectedOrder.total?.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Linked Quote Request Reference */}
              {selectedOrder.from_quote_request && selectedOrder.quote_request_id && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-400 text-sm font-medium">From Quote Request</p>
                      <p className="text-white font-mono font-bold">{selectedOrder.quote_request_id}</p>
                    </div>
                    <Link to={`/admin/quote-requests?highlight=${selectedOrder.quote_request_id}`}>
                      <Button
                        size="sm"
                        className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-none"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        View Request
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => downloadReceipt(selectedOrder.order_id)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/5"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Receipt
                </Button>
                <Button
                  onClick={() => {
                    setViewDialogOpen(false);
                    navigate('/admin/orders');
                  }}
                  className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600]"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View in Orders
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
